<script setup>
import { onMounted, ref } from 'vue'
import { newQuestion, reorderQuestion } from '@/services/questionService.js'
import { cloneQuestion } from '@/components/PollTemplate/Common/utilities.js'

import draggable from 'vuedraggable'
import { library } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faClone, faGripLines, faList, faPencil, faPlus, faTrash, faEye } from '@fortawesome/free-solid-svg-icons'
import { client } from '@/services/fetch.js'
import { toast } from '@/services/toast.js'

library.add(faPlus)
library.add(faPencil)
library.add(faClone)
library.add(faTrash)
library.add(faList)
library.add(faGripLines)
library.add(faEye)

const props = defineProps(['pollTemplate', 'questionGroup', 'canView', 'canEdit', 'canDelete', 'confirmModal'])
const currentGroup = ref([])
const loading = ref(false)
const modal = ref({
  enabled: false,
  title: null,
  fn: null,
  action: null,
  isDelete: false
})

const questionTypes = ref({
  'text': 'Aperta',
  'radio': 'Radio',
  'select': 'Scelta singola',
  'date': 'Data',
  'numeric': 'Numerica',
  'checkbox': 'Checkbox',
  'email': 'E-Mail'
})

const singleQuestionRoute = ref('admin/PollTemplateQuestion')

onMounted(async () => {
  currentGroup.value = props.questionGroup
})

function handleClone(question) {
  let message = 'La duplicazione incrementerà il numero di revisione, procedere?'
  let title = 'Incremento numero revisione'
  if (props.pollTemplate['state'] === 'draft') {
    message = 'La domanda verrà duplicata, procedere?'
    title = 'Duplicazione domanda'
  }
  confirmModal(title, message, copyQuestion, question)
}

function handleDelete(question) {
  let message = 'L\'eliminazione incrementerà il numero di revisione, procedere?'
  let title = 'Incremento numero revisione'
  if (props.pollTemplate['state'] === 'draft') {
    message = 'La domanda verrà eliminata, procedere?'
    title = 'Eliminazione domanda'
  }
  confirmModal(title, message, deleteQuestion, question, true)
}

const confirmModal = async (title, action, fn, question, isDelete = false) => {
  if (!modal.value.enabled) {
    modal.value.action = action
    modal.value.args = question
    modal.value.title = title
    modal.value.fn = fn
    modal.value.enabled = true
    modal.value.isDelete = isDelete
  } else {
    modal.value.fn(modal.value.args)
    resetModal()
  }
}

const resetModal = () => {
  Object.assign(modal.value, { enabled: false, action: '', title: '', args: null, isDelete: false })
}

async function deleteQuestion(question) {
  try {
    loading.value = true
    let url = `/config/templates/polls/api/question/${question.id}`
    await client.delete(url)
    toast.create('Domanda eliminata con successo', 'success')
    loading.value = false
    const indexToRemove = props.questionGroup.questions.findIndex(
      item => item.id === question.id
    )
    if (indexToRemove !== -1) {
      props.questionGroup.questions.splice(indexToRemove, 1)
    }
  } catch (error) {
    loading.value = false
    toast.create('Errore durante l\'eliminazione, riprova', 'error')
  }
}

async function copyQuestion(data) {
  loading.value = true

  const questionCopy = cloneQuestion(data)
  const question = await newQuestion(questionCopy)
  props.questionGroup.questions.push(question)

  loading.value = false
}

async function orderQuestions() {
  await reorderQuestion(currentGroup.value)
}

function showAnswers(answers) {
  modal.value.title = 'Risposte'
  let body = '<ul class="list-group list-group-flush list-group-numbered">'
  answers.forEach((el) => {
    body += '<li class="list-group-item"><span class="fw-bold">' + el['label'] + '</span></li>'
  })
  body += '</ul>'
  modal.value.action = body
  modal.value.enabled = true
}

</script>

<template>
  <div :class="'questionGroup'+currentGroup.id" :ref="'questionGroup'+currentGroup.id">
    <div class="mt-5">
      <h4>Domande</h4>

      <div class="table-responsive">
        <table class="table table-striped table-bordered">
          <thead>
          <tr>
            <th></th>
            <th>
              Testo
            </th>
            <th>
              Tipo
            </th>
            <th class="text-end" v-if="canEdit && currentGroup['canAddQuestions']">
              <router-link v-if="pollTemplate.id && currentGroup.id"
                           :to="{name: 'admin/PollTemplateQuestion', params: {id: pollTemplate.id }, query: {idGroup: currentGroup.id, }}">
                <button class="btn btn-primary btn-sm" title="Aggiungi Domanda">
                  <font-awesome-icon icon="fa-solid fa-plus" />
                </button>
              </router-link>
            </th>
          </tr>
          </thead>

          <draggable
            v-if="currentGroup['questions'] && currentGroup['questions'].length > 0"
            v-model="currentGroup['questions']"
            :disabled="showingRevision || !canEdit"
            tag="tbody"
            item-key="id"
            @end="orderQuestions"
          >
            <template #item="{ element }"
            >
              <tr>
                <td class="handle border-0" style="width: 30px; cursor: grabbing">
                  <font-awesome-icon icon="fa-solid fa-grip-lines" />
                </td>
                <td class="border-0">
                  {{ element.text + (element.required ? ' *' : '') }}
                </td>
                <td class="border-0">
                  {{ questionTypes[element.type] }}
                </td>
                <td class="text-end border-0">
                  <button
                    class="btn btn-primary btn-sm"
                    title="Visualizza risposte"
                    @click="showAnswers(element['additionalProperties']['select_values'])"
                    v-if="(canView || canEdit) && (element.type === 'select' || element.type === 'checkbox' || element.type === 'radio')">
                    <font-awesome-icon icon="fa-solid fa-list" />
                  </button>
                  <router-link
                    v-if="canEdit"
                    :to="{
                      name: singleQuestionRoute,
                      params: {
                        id: pollTemplate.id
                      },
                      query: {
                        idGroup: currentGroup['id'],
                        idQuestion: element.id
                      }
                    }"
                  >
                    <button class="btn btn-primary btn-sm ms-2">
                      <font-awesome-icon
                        v-if="element['enabledActions']['update']"
                        icon="fa-solid fa-pencil" />
                      <font-awesome-icon
                        v-else
                        icon="fa-solid fa-eye" />
                    </button>
                  </router-link>
                  <button v-if="canEdit && element['enabledActions']['update']"
                          title="Duplica domanda"
                          @click="handleClone(element)"
                          class="btn btn-primary btn-sm ms-2">
                    <font-awesome-icon icon="fa-solid fa-clone" />
                  </button>
                  <button v-if="canDelete && element['enabledActions']['delete']"
                          title="Elimina domanda"
                          @click="handleDelete(element)"
                          class="btn btn-danger btn-sm ms-2">
                    <font-awesome-icon icon="fa-solid fa-trash" />
                  </button>
                </td>
              </tr>
            </template>
          </draggable>
          <tbody v-else>
          <tr>
            <td colspan="4" class="text-center">Nessun risultato</td>
          </tr>
          </tbody>

        </table>
      </div>
    </div>

    <div :class="'modal '+ (modal.enabled ? 'd-block' :'')" style="background-color: rgba(0,0,0, 0.8)" id="exampleModal"
         tabindex="-1" aria-labelledby="exampleModalLabel" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h1 class="modal-title fs-5" id="exampleModalLabel">{{ modal.title }}</h1>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"
                    @click="resetModal"></button>
          </div>
          <div class="modal-body" v-html="modal.action">
          </div>
          <div class="modal-footer border-top-0">
            <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal" @click="resetModal">
              Annulla
            </button>
            <button v-if="modal.args" type="button" class="btn" :class="modal.isDelete ? 'btn-danger' : 'btn-primary'"
                    data-bs-dismiss="modal"
                    @click="confirmModal" :disabled="loading">
              {{ modal.isDelete ? 'Elimina' : 'Conferma' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
